package com.oscarherreros.appcalculo.game

const val MAX_STARS = 3

data class Step(val a: Long, val op: Op, val b: Long, val result: Long)

/** La ficha [moved] ha ido del hueco [from] al [to], donde queda el resultado. */
data class Fuse(val from: Int, val to: Int, val moved: Long)

sealed interface Hint {
    /** Siguiente paso de una solución óptima desde la posición actual. */
    data class NextStep(val step: Step) : Hint

    /** Desde la posición actual ya no se puede llegar al objetivo. */
    data object DeadEnd : Hint
}

/**
 * Estado de una partida. Las fichas ocupan posiciones fijas: al operar, la
 * primera deja un hueco (null) y el resultado sustituye a la segunda.
 */
data class GameState(
    val level: Level,
    val tiles: List<Long?> = level.numbers.map { it.toLong() },
    val selected: Int? = null,
    val op: Op? = null,
    val steps: List<Step> = emptyList(),
    val hint: Hint? = null,
    val hintsUsed: Int = 0,
    /** De qué hueco a cuál se ha movido la ficha en cada paso de [steps]. */
    val fuses: List<Fuse> = emptyList(),
    private val previousTiles: List<List<Long?>> = emptyList(),
) {
    val target: Long get() = level.target.toLong()

    val solved: Boolean get() = target in tiles

    /** La última operación hecha; la interfaz la usa para animarla. */
    val lastFuse: Fuse? get() = fuses.lastOrNull()

    /**
     * Puntuación al resolver el nivel: se pierde una estrella por cada operación
     * de más o pista usada.
     */
    val stars: Int
        get() = (MAX_STARS - (steps.size - level.minSteps) - hintsUsed).coerceIn(1, MAX_STARS)

    /** Posiciones de las dos fichas de la pista, en el orden en que hay que tocarlas. */
    val hintedTiles: List<Int>
        get() {
            val step = (hint as? Hint.NextStep)?.step ?: return emptyList()
            val first = tiles.indexOf(step.a)
            val second = tiles.indices.first { it != first && tiles[it] == step.b }
            return listOf(first, second)
        }

    /** Resultado de operar la ficha seleccionada con la de [index], si la jugada es válida. */
    fun preview(index: Int): Long? {
        if (selected == null || op == null || index == selected) return null
        val a = tiles[selected] ?: return null
        val b = tiles[index] ?: return null
        return op.calc(a, b)
    }

    fun tapTile(index: Int): GameState {
        val b = tiles[index]
        if (solved || b == null) return this
        if (index == selected) return copy(selected = null, op = null)
        if (selected == null || op == null) return copy(selected = index)
        val a = tiles[selected] ?: return this
        val result = op.calc(a, b) ?: return this
        // El resultado queda seleccionado para poder encadenar operaciones.
        return copy(
            tiles = tiles.toMutableList().also {
                it[selected] = null
                it[index] = result
            },
            selected = index,
            op = null,
            steps = steps + Step(a, op, b, result),
            hint = null,
            fuses = fuses + Fuse(from = selected, to = index, moved = a),
            previousTiles = previousTiles + listOf(tiles),
        )
    }

    fun tapOp(tapped: Op): GameState = when {
        solved || selected == null -> this
        else -> copy(op = tapped.takeIf { it != op })
    }

    /** Muestra la pista para la posición actual. Solo cuenta como usada si revela un paso. */
    fun requestHint(): GameState {
        if (solved || hint != null) return this
        val next = solve(tiles.filterNotNull(), target)?.firstOrNull()
        return if (next == null) {
            copy(hint = Hint.DeadEnd)
        } else {
            copy(hint = Hint.NextStep(next), hintsUsed = hintsUsed + 1)
        }
    }

    fun undo(): GameState {
        if (steps.isEmpty()) return this
        return copy(
            tiles = previousTiles.last(),
            selected = null,
            op = null,
            steps = steps.dropLast(1),
            hint = null,
            fuses = fuses.dropLast(1),
            previousTiles = previousTiles.dropLast(1),
        )
    }

    fun reset(): GameState = GameState(level)
}
